import { Component, ViewChild, TemplateRef, Input, ChangeDetectionStrategy } from '@angular/core';

/**
 * Tab item component to be used inside {@link TabGroupComponent}.
 *
 * @remarks
 * TabComponent represents an individual tab item within a tab group. Each tab contains
 * content that is displayed when the tab is selected. Tabs can be disabled to prevent
 * user selection.
 */
@Component({
  selector: 'bb-tab-ui',
  templateUrl: './tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabComponent {
  /**
   * Template container reference for the tab content.
   */
  @ViewChild('tabItemTemplate', { static: true }) defaultTemplate: TemplateRef<any> | undefined;
  /**
   * Determines whether the tab can be selected by the user.
   *
   * @remarks
   * When set to `true`, the tab appears disabled and cannot be selected via click or keyboard navigation.
   * Disabled tabs are skipped when navigating with arrow keys.
   *
   * @example
   *
   * ```html
   * <bb-tab-group-ui>
   *   <bb-tab-ui [disabled]="false">
   *     <span>Active Tab</span>
   *   </bb-tab-ui>
   *   <bb-tab-ui [disabled]="true">
   *     <span>Disabled Tab</span>
   *   </bb-tab-ui>
   * </bb-tab-group-ui>
   * ```
   */
  @Input() disabled = false;

  /**
   * Gets the template reference for this tab's content.
   *
   * @returns The TemplateRef containing the tab item template.
   */
  get template() {
    return this.defaultTemplate;
  }
}
